import { Filter } from '@cubejs-client/core';
import { head, trimStart } from 'lodash-es';
import { useMemo } from 'react';

import { CUBE_COMPETITOR_PARTNER_SCORE, CUBE_PARTNER_SCORE, HCP_CUBE_NAME } from '@/api/hcp/interaction/types';
import { toStringOrNull } from '@/utils/cube/mapper';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { CountWithComparison } from '../../../../models';

export type VerbatimComment = {
  name: string;
  commentType: string;
  comment: string;
  company?: string;
  country: string;
  hcpBk: string | null;
};

export enum SatisfactionVerbatimCommentsTypes {
  SATISFIED = 'Satisfied',
  UNSATISFIED = 'Unsatisfied',
}

export const SATISFACTION_VERBATIM_COMMENT: Record<
  keyof typeof SatisfactionVerbatimCommentsTypes | 'All',
  SatisfactionVerbatimCommentsTypes | 'allTypes'
> = {
  SATISFIED: SatisfactionVerbatimCommentsTypes.SATISFIED,
  UNSATISFIED: SatisfactionVerbatimCommentsTypes.UNSATISFIED,
  All: 'allTypes',
};

export type Satisfaction = (typeof SATISFACTION_VERBATIM_COMMENT)[keyof typeof SATISFACTION_VERBATIM_COMMENT];

export const getCountryCode = (hcpBk: string) => {
  const parts = hcpBk.split('^^');
  return trimStart(head(parts));
};

export const getPercentageChange = (count: CountWithComparison): number | null => {
  const current = count?.current;
  const previous = count?.previous;

  if (typeof current !== 'number' || typeof previous !== 'number' || previous === 0) return null;

  return ((current - previous) / previous) * 100;
};

export const isAllQuarters = (quarter: number): boolean => quarter === -1;

enum HCPCubeResources {
  HCP_BK = 'hcp_bk',
  FIRST_NAME = 'first_name',
  LAST_NAME = 'last_name',
  COUNTRY_NAME = 'country_name',
}

export function useNonAnonymousDataFilters(
  globalFiltersWithoutHcpCountryCode: { and: Filter[] } | undefined,
): Filter[] {
  return useMemo(() => {
    if (!globalFiltersWithoutHcpCountryCode || !globalFiltersWithoutHcpCountryCode.and?.length) return [];
    if (globalFiltersWithoutHcpCountryCode?.and?.length > 0) {
      return [
        { member: `${HCP_CUBE_NAME}.${HCPCubeResources.HCP_BK}`, operator: 'notEquals', values: ['null', null] },
        { member: `${HCP_CUBE_NAME}.${HCPCubeResources.LAST_NAME}`, operator: 'notEquals', values: ['null', null] },
        { member: `${HCP_CUBE_NAME}.${HCPCubeResources.FIRST_NAME}`, operator: 'notEquals', values: ['null', null] },
      ];
    }
    return [];
  }, [globalFiltersWithoutHcpCountryCode]);
}

enum CompetitorPartnerScoreCubeResources {
  COMMENT = 'trans_csat_satisfaction_comment',
  ANSWER_HIGH = 'trans_ces_q17_answer_high',
  COMPETITOR = 'competitor',
  COUNTRY_CODE = 'country_code',
  COUNTRY_NAME = 'country_name',
  HCP_BK = 'hcp_bk',
}

export const mapVerbatimCommentData = ({
  verbatimCommentCube,
  verbatimComment,
  verbatimCommentType,
}: {
  verbatimCommentCube: typeof CUBE_PARTNER_SCORE | typeof CUBE_COMPETITOR_PARTNER_SCORE;
  verbatimComment: CubeRawRow;
  verbatimCommentType: string;
}): VerbatimComment => {
  const hcpBk = toStringOrNull(
    verbatimComment[`${verbatimCommentCube}.${CompetitorPartnerScoreCubeResources.HCP_BK}`],
    '',
  );
  const firstName = toStringOrNull(verbatimComment[`${HCP_CUBE_NAME}.${HCPCubeResources.FIRST_NAME}`], '');
  const lastName = toStringOrNull(verbatimComment[`${HCP_CUBE_NAME}.${HCPCubeResources.LAST_NAME}`], '');
  const hcpCountryName = toStringOrNull(verbatimComment[`${HCP_CUBE_NAME}.${HCPCubeResources.COUNTRY_NAME}`], '');
  const competitorCountryName = toStringOrNull(
    verbatimComment[`${verbatimCommentCube}.${CompetitorPartnerScoreCubeResources.COUNTRY_NAME}`],
    '',
  );
  const competitorCountryCode = toStringOrNull(
    verbatimComment[`${verbatimCommentCube}.${CompetitorPartnerScoreCubeResources.COUNTRY_CODE}`],
    '',
  );
  const isAnonymous = !hcpBk || hcpBk === 'null' || hcpBk === '';
  const commentTypeValue = toStringOrNull(verbatimComment[`${verbatimCommentCube}.${verbatimCommentType}`], '');
  const comment = toStringOrNull(
    verbatimComment[`${verbatimCommentCube}.${CompetitorPartnerScoreCubeResources.COMMENT}`] ??
      verbatimComment[`${verbatimCommentCube}.${CompetitorPartnerScoreCubeResources.ANSWER_HIGH}`],
  );
  return {
    hcpBk: isAnonymous ? null : hcpBk || null,
    name: isAnonymous ? '-' : `${lastName}, ${firstName}`.trim() || '-',
    country: hcpCountryName || competitorCountryName || competitorCountryCode || '-',
    company: toStringOrNull(
      verbatimComment[`${verbatimCommentCube}.${CompetitorPartnerScoreCubeResources.COMPETITOR}`],
      '-',
    ),
    commentType: commentTypeValue || SatisfactionVerbatimCommentsTypes.SATISFIED,
    comment: comment ?? '-',
  };
};
